/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import { connect } from 'react-redux';

import { Event, Field, ImpactEnd, ImpactStart, Resolved } from './FeedItems';

const Wrapper = styled.div`
  padding: 1rem;
  white-space: pre-wrap;
`;

class IncidentFeed extends React.Component {
  static propTypes = {
    incident: PropTypes.object,
    missingTranslations: PropTypes.func
  };

  render() {
    const { incident, timezone, missingTranslations } = this.props;

    const feedItems = [];

    const initialIncidentImpact = incident.IncidentImpacts[0];

    incident.IncidentImpacts.map((incidentImpact, index) => {
      const startItem = {
        type: 'IMPACT_START',
        impact: incidentImpact,
        severity: incidentImpact.severity,
        postTime: incidentImpact.startTimeCreatedAt,
        postUpdatedTime: incidentImpact.startTimeModifiedAt,
        dateTime: incidentImpact.startTime
      };

      feedItems.push(startItem);

      if (incidentImpact.endTime) {
        const endItem = {
          type: 'IMPACT_END',
          impact: incidentImpact,
          severity: incidentImpact.severity,
          postTime: incidentImpact.endTimeCreatedAt,
          postUpdatedTime: incidentImpact.endTimeModifiedAt,
          dateTime: incidentImpact.endTime,
          duration: incidentImpact.endTime.diff(incidentImpact.startTime)
        };
        feedItems.push(endItem);
      }

      if (incidentImpact.endTime && index === 0) {
        const endItem = {
          type: 'RESOLVED',
          impact: incidentImpact,
          severity: 'healthy',
          dateTime: incidentImpact.endTime.clone().add(1, 'second')
        };
        feedItems.push(endItem);
      }
    });

    incident.IncidentEvents.map((incidentEvent) => {
      let incidentEventWithinImpact = false;
      
      if (typeof initialIncidentImpact !== 'undefined') {
        const impactStartTime = initialIncidentImpact.startTime || '';
        const impactEndTime = initialIncidentImpact.endTime || '';
        incidentEventWithinImpact = incidentEvent.createdAt.isBetween(impactStartTime, impactEndTime);
      }
      const eventItem = {
        type: 'EVENT',
        event: incidentEvent,
        severity: incidentEventWithinImpact ? incidentEvent.severity : 'neutral',
        updateTime: incidentEvent.updatedAt,
        dateTime: incidentEvent.createdAt || null
      };
      feedItems.push(eventItem);
    });

    feedItems.sort((a, b) => {
      if (a.dateTime === null) return -1;
      if (b.dateTime === null) return 1;

      const diff = b.dateTime.diff(a.dateTime);
      if (diff !== 0) return diff;

      // Show
      if (a.type === 'IMPACT_END') return 1;
      if (b.type === 'IMPACT_END') return -1;

      if (a.type === 'IMPACT_START') return -1;
      if (b.type === 'IMPACT_START') return 1;
    });

    if (incident.message.actionPlan) {
      const eventItem = {
        type: 'FIELD',
        severity: 'neutral',
        field: 'actionPlan',
        fieldOption: incident.message.actionPlan,
        dateTime: moment().utc()
      };

      feedItems.unshift(eventItem);
    }

    if (incident.message.rootCause) {
      const eventItem = {
        type: 'FIELD',
        severity: 'neutral',
        field: 'rootCause',
        fieldOption: incident.message.rootCause,
        dateTime: moment().utc()
      };

      feedItems.unshift(eventItem);
    }

    let currentSeverity = 'healthy';

    for (let i = feedItems.length - 1; i >= 0; i--) {
      const item = feedItems[i]; 
      if (item.severity) {
        currentSeverity = item.severity;
      } if (item.type === 'IMPACT_END') {
        currentSeverity = 'healthy';
      } else {
        item.severity = currentSeverity;
      }
    }


    return (
      <Wrapper>
        { feedItems.map((feedItem, index) => {
          switch (feedItem.type) {
            case 'IMPACT_START': {
              return (
                <ImpactStart
                  feedItem={feedItem}
                  key={`${feedItem.type}-${feedItem.severity}-${index}`}
                  missingTranslations={missingTranslations}
                  region={timezone}
                />
              );
            }
            case 'IMPACT_END': {
              return (
                <ImpactEnd
                  feedItem={feedItem}
                  key={`${feedItem.type}-${feedItem.severity}-${index}`}
                  missingTranslations={missingTranslations}
                  region={timezone}
                />
              );
            }
            case 'EVENT': {
              return (
                <Event
                  feedItem={feedItem}
                  key={`${feedItem.type}-${feedItem.severity}-${index}`}
                  missingTranslations={missingTranslations}
                  region={timezone}
                />
              );
            }
            case 'RESOLVED': {
              return (
                <Resolved
                  feedItem={feedItem}
                  key={`${feedItem.type}-${feedItem.severity}-${index}`}
                  missingTranslations={missingTranslations}
                  region={timezone}
                />
              );
            }

            case 'FIELD': {
              return (
                <Field
                  feedItem={feedItem}
                  key={`${feedItem.type}-${feedItem.severity}-${index}`}
                  missingTranslations={missingTranslations}
                  region={timezone}
                />
              );
            }
            default: { return null; }
          }
        })}
      </Wrapper>
    );
  }
}

export default connect((state) => ({ timezone: state.timezonePicker }))(IncidentFeed);

